// Semantic analytics layer for gfirem.com — sends business-named events to
// PostHog (heatmaps, funnels, session replay) and Microsoft Clarity (visual
// heatmaps + rage-click video).
//
// PostHog + Clarity only initialise on the production hostname (see index.html),
// so `track()` here becomes a safe no-op in preview / local dev — we still
// resolve `getLang()` and `getRoute()` but never send anything to the network.

const isBrowser = typeof window !== "undefined";

const getPostHog = () =>
    isBrowser && typeof window.posthog?.capture === "function"
        ? window.posthog
        : null;

const getClarity = () =>
    isBrowser && typeof window.clarity === "function" ? window.clarity : null;

const getGtag = () =>
    isBrowser && typeof window.gtag === "function" ? window.gtag : null;

const getLang = () => {
    if (!isBrowser) return "en";
    try {
        // LanguageContext persists to this key. Falls back to the URL param, then EN.
        const stored = localStorage.getItem("gfirem.lang");
        if (stored === "es" || stored === "en") return stored;
        const url = new URL(window.location.href);
        const p = url.searchParams.get("lang");
        if (p === "es" || p === "en") return p;
    } catch (_) {
        /* storage/URL unavailable — fall through to default */
    }
    return "en";
};

const getRoute = () => {
    if (!isBrowser) return "/";
    return `${window.location.pathname}${window.location.hash || ""}`;
};

/**
 * Send a named event to PostHog + Clarity.
 * Silently no-ops when SDKs aren't loaded (preview/dev).
 *
 * @param {string} event - snake_case business event name.
 * @param {Record<string, any>} [props] - extra properties for the event.
 */
export function track(event, props = {}) {
    if (!event) return;
    const payload = {
        lang: getLang(),
        route: getRoute(),
        ...props,
    };
    const ph = getPostHog();
    if (ph) {
        try {
            ph.capture(event, payload);
        } catch (_) {
            /* PostHog can throw during teardown — never let it break the page */
        }
    }
    const clarity = getClarity();
    if (clarity) {
        try {
            // Clarity accepts `event` (custom event) and `set` (custom tag).
            // We push the event name and stamp lang as a tag for segmentation.
            clarity("event", event);
            clarity("set", "lang", payload.lang);
            if (payload.source) clarity("set", "source", String(payload.source));
        } catch (_) {
            /* Clarity queue not ready or blocked — no-op */
        }
    }
    const gtag = getGtag();
    if (gtag) {
        try {
            // Translate our internal SPA event to GA4's canonical `page_view`
            // so the built-in Pages / Realtime reports work out of the box.
            if (event === "page_viewed") {
                gtag("event", "page_view", {
                    page_path: payload.path || payload.route,
                    page_title: isBrowser ? document.title : undefined,
                    page_location: isBrowser ? window.location.href : undefined,
                    lang: payload.lang,
                });
            } else {
                gtag("event", event, payload);
            }
        } catch (_) {
            /* gtag not ready or blocked — no-op */
        }
    }
}

/**
 * Walk up from an element to find the closest ancestor with a
 * data-cta-source attribute. Lets us tag Hero/Nav/Pricing/Footer sections
 * once and have every CTA inherit the source automatically.
 */
function resolveSource(el) {
    let node = el;
    while (node && node !== document.body) {
        const src = node.getAttribute?.("data-cta-source");
        if (src) return src;
        node = node.parentElement;
    }
    return "unknown";
}

/**
 * Classify an anchor href into a semantic CTA event. Returns null if the
 * link isn't a tracked CTA.
 */
function classifyHref(href) {
    if (!href) return null;
    if (href.startsWith("mailto:")) return "email_clicked";
    if (href.includes("calendly.com")) return "book_call_clicked";
    if (href.includes("wa.me/") || href.includes("api.whatsapp.com"))
        return "whatsapp_clicked";
    if (href.includes("linkedin.com/")) return "linkedin_clicked";
    if (href.includes("github.com/")) return "github_clicked";
    return null;
}

/**
 * Global click listener — auto-tracks CTA clicks by href without needing
 * onClick on every anchor. Attached once on module import.
 */
function initClickAutocapture() {
    if (!isBrowser) return;
    if (window.__gfiremAnalyticsBooted) return;
    window.__gfiremAnalyticsBooted = true;

    document.addEventListener(
        "click",
        (e) => {
            const anchor = e.target.closest?.("a[href]");
            if (!anchor) return;
            const href = anchor.getAttribute("href") || "";
            const event = classifyHref(href);
            if (!event) return;
            track(event, {
                source: resolveSource(anchor),
                href,
                text: (anchor.textContent || "").trim().slice(0, 80),
            });
        },
        // Use capture so we still fire when the anchor stops propagation.
        { capture: true }
    );
}

initClickAutocapture();
